import { type FormEvent, useState } from 'react';
import { apiClient, type OgpPreview } from '../lib/api-client';
import { generateImageUrl } from '../utils/image-url';

const INPUT_CLASS =
  'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400';

function describeError(err: unknown): string {
  if (!(err instanceof Error)) return 'Failed to fetch OGP';
  const details = (err as Error & { details?: unknown }).details;
  return typeof details === 'string' ? `${err.message}: ${details}` : err.message;
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/** R2 に保存した画像を優先し、なければ元の画像 URL を使う */
function previewImageUrl(preview: OgpPreview): string | undefined {
  if (preview.storage) {
    return generateImageUrl({ key: preview.storage.key }) || preview.imageUrl;
  }
  return preview.imageUrl;
}

function OgpCard({ preview }: { preview: OgpPreview }) {
  const imageUrl = previewImageUrl(preview);

  return (
    <a
      href={preview.url}
      target='_blank'
      rel='noopener noreferrer'
      className='block overflow-hidden rounded-lg border border-gray-200 bg-white transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700'
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt={preview.title ?? ''}
          className='aspect-[1.91/1] w-full bg-gray-100 object-cover dark:bg-gray-900'
        />
      )}
      <div className='space-y-1 p-4'>
        <p className='text-xs text-gray-500 dark:text-gray-400'>
          {preview.siteName ?? hostnameOf(preview.url)}
        </p>
        <p className='font-semibold text-gray-900 dark:text-white'>
          {preview.title ?? '(no title)'}
        </p>
        {preview.description && (
          <p className='line-clamp-3 text-sm text-gray-600 dark:text-gray-300'>
            {preview.description}
          </p>
        )}
      </div>
    </a>
  );
}

function StorageDetails({ preview }: { preview: OgpPreview }) {
  if (preview.storageError) {
    return (
      <div className='rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'>
        Failed to store the OGP image: {preview.storageError}
      </div>
    );
  }

  if (!preview.storage) {
    return (
      <p className='text-sm text-gray-600 dark:text-gray-400'>
        This page has no OGP image, so nothing was stored.
      </p>
    );
  }

  const rows: Array<[string, string]> = [
    ['Storage key', preview.storage.key],
    ['Size', `${preview.storage.size.toLocaleString()} bytes`],
    ['MIME type', preview.storage.mimeType],
    ['Original image URL', preview.imageUrl ?? '']
  ];

  return (
    <dl className='grid grid-cols-1 gap-x-4 gap-y-2 text-sm md:grid-cols-[auto_1fr]'>
      {rows.map(([label, value]) => (
        <div key={label} className='contents'>
          <dt className='font-medium text-gray-700 dark:text-gray-300'>{label}</dt>
          <dd className='break-all text-gray-900 dark:text-white'>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function OgpPage() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<OgpPreview | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      setPreview(await apiClient.fetchOgp(url.trim()));
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='mx-auto max-w-3xl'>
      <div className='mb-6'>
        <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>OGP</h1>
        <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
          Fetch the OGP of a URL and store its image in object storage (ogp-cache/). Stored images
          are reused as link card thumbnails for scheduled posts.
        </p>
      </div>

      <div className='mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800'>
        <form onSubmit={e => void handleSubmit(e)} className='flex flex-col gap-3 md:flex-row'>
          <input
            type='url'
            required
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder='https://example.com/article'
            className={INPUT_CLASS}
          />
          <button
            type='submit'
            disabled={loading || !url.trim()}
            className='rounded-md bg-blue-600 px-4 py-2 whitespace-nowrap text-white transition-colors hover:bg-blue-700 disabled:bg-gray-400 dark:bg-blue-500 dark:hover:bg-blue-600 dark:disabled:bg-gray-600'
          >
            {loading ? 'Fetching...' : 'Fetch & store'}
          </button>
        </form>
      </div>

      {error && (
        <div className='mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900 dark:text-red-200'>
          {error}
        </div>
      )}

      {preview && (
        <div className='space-y-6'>
          <div className='rounded-lg bg-white p-6 shadow dark:bg-gray-800'>
            <h2 className='mb-4 text-lg font-semibold text-gray-900 dark:text-white'>Preview</h2>
            <OgpCard preview={preview} />
          </div>
          <div className='rounded-lg bg-white p-6 shadow dark:bg-gray-800'>
            <h2 className='mb-4 text-lg font-semibold text-gray-900 dark:text-white'>Storage</h2>
            <StorageDetails preview={preview} />
          </div>
        </div>
      )}
    </div>
  );
}
